import { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "react-toastify";
import { oneKlicks } from "../SDK/oneclick";
import { getSettings } from "../services/settingsService";
import { useAuth } from "../context/AuthContext";

const DEFAULT_COMPANY_ID = Number(process.env.REACT_APP_DEFAULT_INSURANCE_COMPANY_ID || 0) || null;
const FALLBACK_KEY = "rzp_live_SxrHxAcaqcutXT";

const sanitizeAmount = (value) => {
    if (typeof value === "number") {
        return value;
    }
    if (typeof value === "string") {
        const cleaned = value.replace(/[^0-9.]/g, "");
        const numeric = parseFloat(cleaned);
        return Number.isNaN(numeric) ? 0 : numeric;
    }
    if (typeof value === "object" && value !== null && "amount" in value) {
        return sanitizeAmount(value.amount);
    }
    return 0;
};

const PAYMENT_LIMIT = Number(process.env.REACT_APP_INSURANCE_PAYMENT_MAX) || 2000;

const formatInr = (value) => {
    if (!Number.isFinite(value)) {
        return value;
    }
    return new Intl.NumberFormat("en-IN", {
        style: "currency",
        currency: "INR",
        maximumFractionDigits: 0,
    }).format(value);
};

const normalizeInt = (value, fallback = 0) => {
    const numeric = Number(value);
    return Number.isFinite(numeric) ? numeric : fallback;
};

const REQUIRED_COLUMNS = ["policy_id", "customer_id", "agent_id", "payment_date", "amount", "payment_method", "receipt_number", "status"];

const useInsurancePayment = () => {
    const { userData } = useAuth();
    const [razorpayReady, setRazorpayReady] = useState(false);
    const [processingKey, setProcessingKey] = useState(null);
    const [razorpayKey, setRazorpayKey] = useState(
        process.env.REACT_APP_RAZORPAY_KEY_ID || window.__RAZORPAY_KEY_ID__ || FALLBACK_KEY
    );

    useEffect(() => {
        if (typeof window === "undefined") {
            return;
        }

        if (window.__RAZORPAY_READY__) {
            setRazorpayReady(true);
            return;
        }

        if (window.__RAZORPAY_LOADING__) {
            const intervalId = setInterval(() => {
                if (window.__RAZORPAY_READY__) {
                    setRazorpayReady(true);
                    clearInterval(intervalId);
                }
            }, 200);
            return () => clearInterval(intervalId);
        }

        window.__RAZORPAY_LOADING__ = true;
        const script = document.createElement("script");
        script.src = "https://checkout.razorpay.com/v1/checkout.js";
        script.async = true;
        script.onload = () => {
            window.__RAZORPAY_READY__ = true;
            setRazorpayReady(true);
        };
        script.onerror = () => {
            toast.error("Unable to load payment gateway. Please refresh and try again.");
            window.__RAZORPAY_LOADING__ = false;
        };
        document.body.appendChild(script);
    }, []);

    useEffect(() => {
        let mounted = true;
        const fetchSettings = async () => {
            try {
                const settings = await getSettings();
                const keyFromSettings =
                    settings?.razorpay_key_id ||
                    settings?.razorpay_keyid ||
                    settings?.razorpayKey ||
                    settings?.razorpay_public_key;
                if (mounted && keyFromSettings) {
                    setRazorpayKey(keyFromSettings);
                    window.__RAZORPAY_KEY_ID__ = keyFromSettings;
                }
            } catch (error) {
                console.warn("Unable to load Razorpay key from settings", error);
            }
        };
        fetchSettings();
        return () => {
            mounted = false;
        };
    }, []);

    const readCustomerRecord = useCallback(async (conditions) => {
        try {
            const response = await oneKlicks.read(
                "",
                "insurance_customers",
                conditions,
                "id DESC",
                "",
                "",
                "0,1"
            );

            const rows = Array.isArray(response?.data)
                ? response.data
                : Array.isArray(response?.data?.items)
                ? response.data.items
                : [];

            return rows.length ? rows[0] : null;
        } catch (error) {
            console.warn("Unable to read insurance customer", conditions, error);
            return null;
        }
    }, []);

    const ensureCustomerRecordId = useCallback(
        async (policyMeta, companyId, timestamp) => {
            const candidateIds = [
                policyMeta?.customer_table_id,
                policyMeta?.customer_record_id,
                policyMeta?.customer_id,
                policyMeta?.customerId,
                policyMeta?.customer?.id,
            ]
                .map((value) => normalizeInt(value, 0))
                .filter((value) => value > 0);

            for (const candidate of candidateIds) {
                const existingRecord = await readCustomerRecord({ id: candidate });
                if (existingRecord?.id) {
                    return existingRecord.id;
                }
            }

            if (userData?.id) {
                const byUserRecord = await readCustomerRecord({ user_id: userData.id });
                if (byUserRecord?.id) {
                    return byUserRecord.id;
                }
            }

            const newCustomerPayload = {
                user_id: userData?.id || null,
                company_id: companyId || null,
                name: policyMeta?.policy_holder_name || userData?.display_name || "Insurance Customer",
                email: userData?.email || null,
                phone: userData?.phone || null,
                status: "active",
                created_at: timestamp,
                updated_at: timestamp,
            };

            try {
                const createResponse = await oneKlicks.create(
                    "",
                    "insurance_customers",
                    newCustomerPayload
                );

                if (createResponse?.success) {
                    return (
                        createResponse.insertId ||
                        createResponse?.data?.id ||
                        candidateIds[0] ||
                        0
                    );
                }
            } catch (error) {
                console.error("Unable to create insurance customer", newCustomerPayload, error);
            }

            return normalizeInt(userData?.insurance_customer_id || userData?.id, 0);
        },
        [readCustomerRecord, userData]
    );

    const recordPayment = useCallback(
        async ({ amount, policyMeta, razorpayResponse, notes }) => {
            const timestamp = new Date().toISOString().slice(0, 19).replace("T", " ");
            const paymentDate = timestamp.slice(0, 10);
            const receiptNumber =
                policyMeta?.policyNumber && timestamp
                    ? `${policyMeta.policyNumber}-${timestamp.replace(/[^0-9]/g, "")}`
                    : `PAY-${timestamp.replace(/[^0-9]/g, "")}`;

            const normalizedPolicyId = normalizeInt(
                policyMeta?.policyId || policyMeta?.id,
                0
            );
            const companyIdentifier =
                policyMeta?.companyId ||
                policyMeta?.company_id ||
                userData?.company_id ||
                userData?.companyId ||
                DEFAULT_COMPANY_ID;

            const normalizedCustomerId = await ensureCustomerRecordId(
                policyMeta,
                companyIdentifier,
                timestamp
            );
            const normalizedAgentId = normalizeInt(
                policyMeta?.agentId ||
                    policyMeta?.agent_id ||
                    policyMeta?.agent?.id ||
                    userData?.agent_id ||
                    userData?.agentId ||
                    userData?.id,
                0
            );

            const payload = {
                policy_id: normalizedPolicyId,
                policy_number: policyMeta?.policyNumber || policyMeta?.referenceId || null,
                policy_holder_name: userData?.display_name || userData?.name || null,
                customer_id: normalizedCustomerId,
                agent_id: normalizedAgentId,
                customer_name: userData?.display_name || userData?.name || null,
                amount,
                payment_method: "online",
                status: "completed",
                payment_date: paymentDate,
                receipt_number: receiptNumber,
                transaction_id: razorpayResponse?.razorpay_payment_id || null,
                provider_reference: razorpayResponse?.razorpay_order_id || null,
                razorpay_signature: razorpayResponse?.razorpay_signature || null,
                company_id: policyMeta?.companyId || userData?.company_id || DEFAULT_COMPANY_ID,
                product_name: policyMeta?.product || policyMeta?.coverageType || null,
                insurer_name: policyMeta?.insurer || null,
                notes: notes || policyMeta?.notes || null,
                created_at: timestamp,
                updated_at: timestamp,
            };

            const missingRequired = REQUIRED_COLUMNS.filter((column) => {
                const value = payload[column];
                if (column === "amount") {
                    return !(Number.isFinite(Number(value)) && Number(value) > 0);
                }
                return value === null || value === undefined || value === "";
            });

            if (missingRequired.length > 0) {
                console.error("Missing required payment fields", { missingRequired, payload });
                throw new Error(`Missing required payment fields: ${missingRequired.join(", ")}`);
            }

            let response;
            try {
                response = await oneKlicks.create("", "insurance_payments", payload);
            } catch (apiError) {
                console.error("Insurance payment API error", { payload, apiError });
                throw apiError;
            }

            if (!response?.success) {
                console.error("Insurance payment API response error", { payload, response });
                throw new Error(response?.message || response?.error || "Unable to record payment");
            }
        },
        [userData]
    );

    const startPayment = useCallback(
        async ({ referenceId, amount, policyMeta = {}, description }) => {
            if (!userData?.id) {
                toast.error("Please login to continue with payment.");
                return;
            }

            if (!razorpayReady || typeof window === "undefined" || typeof window.Razorpay === "undefined") {
                toast.error("Payment gateway is still loading. Please try again in a moment.");
                return;
            }

            const payableAmount = sanitizeAmount(amount);
            if (!payableAmount) {
                toast.error("Unable to determine payment amount for this policy.");
                return;
            }

            const companyIdentifier = policyMeta?.companyId || userData?.company_id || DEFAULT_COMPANY_ID;
            if (!companyIdentifier) {
                toast.warn("Missing company details for this policy. Please try again later.");
                return;
            }

            const paymentCap = Number.isFinite(PAYMENT_LIMIT) && PAYMENT_LIMIT > 0 ? PAYMENT_LIMIT : null;
            const chargeAmount = paymentCap ? Math.min(payableAmount, paymentCap) : payableAmount;
            if (paymentCap && payableAmount > paymentCap) {
                toast.info(
                    `Charging ${formatInr(chargeAmount)} (capped for test mode; original premium ${formatInr(
                        payableAmount
                    )}).`
                );
            }

            setProcessingKey(referenceId);

            const resolvedKey = razorpayKey || process.env.REACT_APP_RAZORPAY_KEY_ID || window.__RAZORPAY_KEY_ID__;
            if (!resolvedKey) {
                toast.error("Payment gateway is not configured. Please contact support.");
                setProcessingKey(null);
                return;
            }

            const options = {
                key: resolvedKey,
                amount: Math.round(chargeAmount * 100),
                currency: "INR",
                name: policyMeta.checkoutTitle || "MeddyList Insurance",
                description:
                    description || `Payment for ${policyMeta.product || policyMeta.policyNumber || "insurance plan"}`,
                prefill: {
                    name: userData?.display_name || userData?.name || "",
                    email: userData?.email || "",
                    contact: userData?.phone || "",
                },
                notes: {
                    policyId: policyMeta?.policyId || "",
                    policyNumber: policyMeta?.policyNumber || "",
                    companyId: companyIdentifier,
                    userId: userData?.id || "",
                    referenceId,
                },
                method: {
                    card: true,
                    netbanking: true,
                    upi: true,
                    wallet: false,
                    emi: false,
                    paylater: false,
                },
                config: {
                    display: {
                        blocks: {
                            card: {
                                name: "Cards",
                                instruments: [{ method: "card" }],
                            },
                            netbanking: {
                                name: "Netbanking",
                                instruments: [{ method: "netbanking" }],
                            },
                            upi: {
                                name: "UPI",
                                instruments: [{ method: "upi" }],
                            },
                            paylater: {
                                name: "Pay Later",
                                instruments: [{ method: "paylater" }],
                            },
                            emi: {
                                name: "EMI",
                                instruments: [{ method: "emi" }],
                            },
                        },
                        sequence: ["block.card", "block.netbanking", "block.upi"],
                        preferences: {
                            show_default_blocks: false,
                        },
                    },
                },
                theme: { color: policyMeta?.themeColor || "#0b4fe0" },
                handler: async (response) => {
                    const combinedNotes = [
                        policyMeta?.notes,
                        description,
                        chargeAmount !== payableAmount
                            ? `Original monthly premium ${formatInr(payableAmount)} (charged ${formatInr(chargeAmount)})`
                            : null,
                    ]
                        .filter(Boolean)
                        .join(" | ");

                    try {
                        await recordPayment({
                            amount: chargeAmount,
                            policyMeta: { ...policyMeta, companyId: companyIdentifier, referenceId },
                            razorpayResponse: response,
                            notes: combinedNotes || description,
                        });
                        toast.success("Payment recorded successfully!");
                        policyMeta?.onSuccess?.(response);
                    } catch (error) {
                        console.error("Error recording payment", error);
                        toast.error("Payment processed but failed to save history. Please contact support.");
                    } finally {
                        setProcessingKey(null);
                    }
                },
                modal: {
                    ondismiss: () => {
                        setProcessingKey(null);
                        policyMeta?.onDismiss?.();
                    },
                },
            };

            try {
                const razorpayInstance = new window.Razorpay(options);
                razorpayInstance.on("payment.failed", (response) => {
                    console.error("Payment failed", response?.error);
                    toast.error(response?.error?.description || "Payment failed. Please try again.");
                    setProcessingKey(null);
                    policyMeta?.onFailure?.(response);
                });
                razorpayInstance.open();
            } catch (error) {
                console.error("Unable to start Razorpay checkout", error);
                toast.error("Unable to initiate payment. Please try again later.");
                setProcessingKey(null);
            }
        },
        [userData, razorpayReady, recordPayment]
    );

    return useMemo(
        () => ({
            startPayment,
            processingKey,
            razorpayReady,
        }),
        [processingKey, razorpayReady, startPayment]
    );
};

export default useInsurancePayment;

